import {useStore} from '@/common/store/store.js';
import {useApiClient} from '@/common/api/useApiClient.js';
import {ref} from 'vue';
import {initialized} from '@/composables/layout/layoutHandler.js';
import {reloadPage} from '@/common/utils/windowUtils.js';
import {oidcClient} from '@/common/auth/oidcClient.js';
import {UserSwitchApi} from '@/common/api/user/UserSwitchApi.js';
import {LoginUser} from '@/types/LoginUser.js';
import {clearUserSwitchTarget, readUserSwitchTarget} from '@/common/auth/userSwitchState.js';
import {clearApiCache} from '@/common/api/apiCache.js';

// Guards against re-entrancy: the re-exchange below itself calls the API, whose
// 401 handler is refresh() again — a fresh admin token never 401s, but the flag
// makes the recursion impossible even if it did.
let reexchanging = false;

/**
 * OIDC — einziger Login-Pfad. Login/2FA laufen server-seitig im Interaction-Flow
 * der API; der Browser macht nur den Full-Page-Redirect und verarbeitet den
 * Rückkanal. Der user-switch bleibt auf den REST-Endpunkten `/user/switch*`
 * (die jetzt kurzlebige OIDC-Tokens liefern) und ist bewusst NICHT hier.
 */
export function useAuth() {
  const store = useStore();
  const apiClient = useApiClient();
  const loading = ref(false);

  // OIDC — Login als Full-Page-Redirect auf den API-Login; `uiLocales` = App-Sprache,
  // damit die server-gerenderte Login-Seite in derselben Sprache erscheint.
  const loginWithOidc = async (uiLocales?: string): Promise<void> => {
    await oidcClient.login(uiLocales);
  };

  // OIDC — Rückkanal nach dem Login: Code→Token, dann das Access-Token in den Store
  // spiegeln (die App liest es unverändert aus dem Store). Die User-Daten lädt danach
  // der App-Bootstrap über initialize()/user-data, sobald isLoggedIn greift.
  const handleOidcCallback = async (): Promise<boolean> => {
    const user = await oidcClient.handleCallback();
    if (user?.access_token) {
      // A new login may be another account than the one before in this tab
      // (e.g. after an expired session): start without its cached answers.
      clearApiCache();
      store.setAccessToken(user.access_token);
      return true;
    }
    return false;
  };

  const logout = async () => {
    // WICHTIG: hier NICHT store.logout() vorab! Das würde accessToken=null setzen →
    // App.vue leitet reaktiv auf SignIn um, das sofort einen neuen Authorize-Redirect
    // startet und mit signoutRedirect um die Vollseiten-Navigation rennt. Bei
    // Prod-Latenz gewinnt der Auto-Login → man wird still gegen die noch lebende
    // OP-Sitzung neu eingeloggt. Deshalb DIREKT den RP-Logout starten; dessen
    // Navigation zum OP-Logout räumt den lokalen Zustand ohnehin ab.
    initialized.value = false;
    // Beim echten Logout die durable Switch-Absicht verwerfen — sonst würde die
    // App beim nächsten Login den alten Switch wiederherstellen ("komischer Zustand").
    clearUserSwitchTarget();
    // The cached answers belong to this account; the next one in this tab must
    // not see them (T00N1NIEPQBA).
    clearApiCache();
    await oidcClient.logout().catch(() => {
      // Nur wenn der Logout-Redirect fehlschlägt: lokal aufräumen + neu laden.
      store.logout();
      apiClient.logout();
      reloadPage();
    });
  };

  // Re-derives the user-switch token from a fresh admin token. The switch token is
  // access-only and lives only in memory, so on every token acquisition (reload or
  // a 401-triggered refresh) we re-exchange the stored target — otherwise the admin
  // token would silently replace the switched session. Stale/denied targets are
  // dropped, falling back to the admin session.
  const reexchangeUserSwitch = async (): Promise<string | null> => {
    const target = readUserSwitchTarget();
    if (!target || reexchanging) {
      return null;
    }
    reexchanging = true;
    try {
      const response = await apiClient.post<LoginUser>(UserSwitchApi.api(), {user: target.user, customer: target.customer});
      if (response.status === 200) {
        store.setUserData(response.data.user);
        store.setAccessToken(response.data.accessToken);
        return response.data.accessToken;
      }
      clearUserSwitchTarget();
    } catch {
      clearUserSwitchTarget();
    } finally {
      reexchanging = false;
    }
    return null;
  };

  const refresh = async (): Promise<string | null> => {
    // OIDC — Token-Erneuerung übernimmt oidc-client-ts: erst einen noch gültigen
    // gespeicherten Token nutzen, sonst Silent-Renew.
    const existing = await oidcClient.getValidAccessToken();
    const token = existing ?? await oidcClient.renew();
    if (!token) {
      return null;
    }
    store.setAccessToken(token);
    // Aktiver user-switch? Aus dem frischen Admin-Token einen Switch-Token ableiten,
    // damit der Switch Reloads UND 401-Refreshes übersteht.
    const switched = await reexchangeUserSwitch();
    return switched ?? token;
  };

  return {
    loginWithOidc,
    handleOidcCallback,
    logout,
    refresh,
    loading,
    isLoggedIn: () => !!store.accessToken,
  };
}
